export const CHAT_TIMEOUT_MS = 30000;
export const SUMMARY_TIMEOUT_MS = 90000;
export const INSIGHT_TIMEOUT_MS = 90000;
export const CHAT_OUTPUT_TOKENS = 600;
export const DEEP_DISCUSS_OUTPUT_TOKENS = 1200;
export const CONTEXT_SELECT_OUTPUT_TOKENS = 800;
export const SUMMARY_OUTPUT_TOKENS = 4000;
export const INSIGHT_OUTPUT_TOKENS = 2500;
export const MAX_MESSAGES_PER_ROOM = 20;
export const MESSAGE_WARNING_THRESHOLD = 18;
export const INPUT_MEMO_SUGGESTION_INTERVAL = 10;
// Probレポート連携のinputルームでは、メモ提案の間隔を広げる（会話が長くなりやすいため）。
export const PROB_INPUT_MEMO_SUGGESTION_INTERVAL = 20;
// Probレポート引用ブロックの先頭マーカー。サマリープロンプトの出力とMarkdownBlockの判定で共有する。
export const PROB_QUOTE_PREFIX = 'Probレポート「';
export const MAX_TITLE_LENGTH = 40;
export const MAX_TAGS_PER_DOC = 10;
export const LONG_PASTE_CHAR_THRESHOLD = 1000;
export const SINK_INPUT_CHAR_LIMIT = 50000;
export const AI_ACTIVE_INPUT_CHAR_LIMIT = 4000;

export const IS_MOCK_DEMO = import.meta.env.VITE_DEMO_MODE === 'mock' || (typeof window !== 'undefined' && window.location.pathname.includes('/demo'));

export const ASSET_TYPE = Object.freeze({
  STANCE: 'stance',
  FRAMEWORK: 'framework',
  PREFERENCE: 'preference',
  THEME: 'theme',
  QUESTION: 'question',
  TODO: 'todo',
  PROJECT: 'project',
  KNOWLEDGE: 'knowledge',
  INTEREST: 'interest',
});

export const ASSET_STATUS = Object.freeze({
  DRAFT: 'draft',
  ACTIVE: 'active',
  DONE: 'done',
  PAUSED: 'paused',
  ARCHIVED: 'archived',
  DEPRECATED: 'deprecated',
  PENDING: 'pending',
});

export const ASSET_MATURITY = Object.freeze({
  SEED: 'seed',
  GROWING: 'growing',
  CRYSTALLIZED: 'crystallized',
});

export const NOTE_STATUS = Object.freeze({
  INBOX: 'inbox',
  REVIEWED: 'reviewed',
  LINKED: 'linked',
  ARCHIVED: 'archived',
});

export const INSIGHT_STATUS = Object.freeze({
  SUGGESTED: 'suggested',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  MERGED: 'merged',
});

export const INSIGHT_KIND = Object.freeze({
  HINT: 'hint',
  PROPOSED_CHANGE: 'proposed_change',
});

export const INSIGHT_TYPE = Object.freeze({
  IMPLICIT_KNOWLEDGE: 'implicit_knowledge',
  CONFLICT: 'conflict',
  MISSING_LINK: 'missing_link',
  SYNTHESIS_SUGGESTION: 'synthesis_suggestion',
  DECAY_WARNING: 'decay_warning',
  NOTE_UPDATE: 'note_update',
  ASSET_UPDATE: 'asset_update',
  TAG_MERGE: 'tag_merge',
  DUPLICATE_NOTE: 'duplicate_note',
  DUPLICATE_ASSET: 'duplicate_asset',
  DUPLICATE_INSIGHT: 'duplicate_insight',
});

export const FIRESTORE_INSIGHT_KINDS = [
  INSIGHT_KIND.HINT,
  INSIGHT_KIND.PROPOSED_CHANGE,
];

export const CHAT_ROOM_STATUS = Object.freeze({
  ACTIVE: 'active',
  SUMMARIZED: 'summarized',
  ASSETIZED: 'assetized',
  ARCHIVED: 'archived',
});

export const CHAT_MESSAGE_KIND = Object.freeze({
  RAW_NOTE: 'raw_note',
  CHAT_REPLY: 'chat_reply',
  MEMO_SUGGESTION: 'memo_suggestion',
  ERROR: 'error',
});

// Canonical Firestore values from requirement_definition.md.
// `interest` remains a compatibility alias in the UI layer, but is intentionally excluded here
// because it is not part of the current Firestore data model.
export const FIRESTORE_ASSET_TYPES = [
  ASSET_TYPE.STANCE,
  ASSET_TYPE.FRAMEWORK,
  ASSET_TYPE.PREFERENCE,
  ASSET_TYPE.THEME,
  ASSET_TYPE.QUESTION,
  ASSET_TYPE.TODO,
  ASSET_TYPE.PROJECT,
  ASSET_TYPE.KNOWLEDGE,
];

// Canonical Firestore statuses intentionally exclude the legacy `pending` status.
// The app still exposes `pending` in some compatibility paths/status filters, so keep that in
// ASSET_STATUS and ASSET_STATUS_OPTIONS_BY_TYPE without treating it as a canonical Firestore value.
export const FIRESTORE_ASSET_STATUSES = [
  ASSET_STATUS.DRAFT,
  ASSET_STATUS.ACTIVE,
  ASSET_STATUS.DONE,
  ASSET_STATUS.PAUSED,
  ASSET_STATUS.ARCHIVED,
  ASSET_STATUS.DEPRECATED,
];

export const FIRESTORE_ASSET_MATURITIES = [
  ASSET_MATURITY.SEED,
  ASSET_MATURITY.GROWING,
  ASSET_MATURITY.CRYSTALLIZED,
];

export const FIRESTORE_NOTE_STATUSES = [
  NOTE_STATUS.INBOX,
  NOTE_STATUS.REVIEWED,
  NOTE_STATUS.LINKED,
  NOTE_STATUS.ARCHIVED,
];

export const FIRESTORE_INSIGHT_STATUSES = [
  INSIGHT_STATUS.SUGGESTED,
  INSIGHT_STATUS.ACCEPTED,
  INSIGHT_STATUS.REJECTED,
  INSIGHT_STATUS.MERGED,
];

export const FIRESTORE_INSIGHT_TYPES = [
  INSIGHT_TYPE.IMPLICIT_KNOWLEDGE,
  INSIGHT_TYPE.CONFLICT,
  INSIGHT_TYPE.MISSING_LINK,
  INSIGHT_TYPE.SYNTHESIS_SUGGESTION,
  INSIGHT_TYPE.DECAY_WARNING,
  INSIGHT_TYPE.NOTE_UPDATE,
  INSIGHT_TYPE.ASSET_UPDATE,
  INSIGHT_TYPE.TAG_MERGE,
  INSIGHT_TYPE.DUPLICATE_NOTE,
  INSIGHT_TYPE.DUPLICATE_ASSET,
  INSIGHT_TYPE.DUPLICATE_INSIGHT,
];

export const FIRESTORE_CHAT_ROOM_STATUSES = [
  CHAT_ROOM_STATUS.ACTIVE,
  CHAT_ROOM_STATUS.SUMMARIZED,
  CHAT_ROOM_STATUS.ASSETIZED,
  CHAT_ROOM_STATUS.ARCHIVED,
];

export const FIRESTORE_CHAT_MESSAGE_KINDS = [
  CHAT_MESSAGE_KIND.RAW_NOTE,
  CHAT_MESSAGE_KIND.CHAT_REPLY,
  CHAT_MESSAGE_KIND.MEMO_SUGGESTION,
  CHAT_MESSAGE_KIND.ERROR,
];

export const ACTION_TYPE = Object.freeze({
  CHAT_REPLY: 'chat_reply',
  CONTEXT_SELECT: 'context_select',
  MEMOIZE: 'memoize',
  EXTRACT_INSIGHT: 'extract_insight',
  TAG_EXTRACT_INSIGHT: 'tag_extract_insight',
  NOTE_UPDATE: 'note_update',
  ASSET_UPDATE: 'asset_update',
  AUDIT: 'audit',
});

export const ERROR_FINISH_REASON = 'error';

export const ASSET_TYPE_LABELS = {
  [ASSET_TYPE.STANCE]: '判断基準',
  [ASSET_TYPE.FRAMEWORK]: 'フレームワーク',
  [ASSET_TYPE.PREFERENCE]: '嗜好',
  [ASSET_TYPE.KNOWLEDGE]: '知識',
  [ASSET_TYPE.TODO]: 'ToDo',
  [ASSET_TYPE.PROJECT]: 'プロジェクト',
  [ASSET_TYPE.INTEREST]: '関心・問い',
};

export const ASSET_TYPES = Object.keys(ASSET_TYPE_LABELS);

export const ASSET_TYPE_DESCRIPTIONS = {
  [ASSET_TYPE.STANCE]: '物事を判断する際の基準や価値観です。迷った時の指針になります。',
  [ASSET_TYPE.FRAMEWORK]: '思考や作業の手順・枠組みです。繰り返し使える方法論をまとめます。',
  [ASSET_TYPE.PREFERENCE]: '好き嫌いやこだわりです。自分らしさを形作るデータです。',
  [ASSET_TYPE.THEME]: '継続的に気になっているテーマや領域です。',
  [ASSET_TYPE.QUESTION]: '後で答えを深掘りしたい問いです。',
  [ASSET_TYPE.PROJECT]: '複数の行動を束ねる取り組みです。',
  [ASSET_TYPE.KNOWLEDGE]: '事実や客観的な情報です。汎用性の高い知識ベースとして蓄積します。',
  [ASSET_TYPE.TODO]: '単発で終わる具体的な行動項目です。',
  [ASSET_TYPE.INTEREST]: '関心テーマや、まだ答えが出ていない重要な問いです。',
};

export const ASSET_STATUS_LABELS = {
  [ASSET_STATUS.DRAFT]: '下書き',
  [ASSET_STATUS.ACTIVE]: '使用中',
  [ASSET_STATUS.PENDING]: '保留',
  [ASSET_STATUS.DONE]: '完了',
  [ASSET_STATUS.PAUSED]: '一時停止',
  [ASSET_STATUS.ARCHIVED]: '保管',
  [ASSET_STATUS.DEPRECATED]: '不採用',
};

export const ASSET_STATUS_OPTIONS_BY_TYPE = {
  [ASSET_TYPE.STANCE]: [ASSET_STATUS.DRAFT, ASSET_STATUS.ACTIVE, ASSET_STATUS.PENDING, ASSET_STATUS.ARCHIVED, ASSET_STATUS.DEPRECATED],
  [ASSET_TYPE.FRAMEWORK]: [ASSET_STATUS.DRAFT, ASSET_STATUS.ACTIVE, ASSET_STATUS.PENDING, ASSET_STATUS.ARCHIVED, ASSET_STATUS.DEPRECATED],
  [ASSET_TYPE.PREFERENCE]: [ASSET_STATUS.DRAFT, ASSET_STATUS.ACTIVE, ASSET_STATUS.PENDING, ASSET_STATUS.ARCHIVED, ASSET_STATUS.DEPRECATED],
  [ASSET_TYPE.THEME]: [ASSET_STATUS.DRAFT, ASSET_STATUS.ACTIVE, ASSET_STATUS.PENDING, ASSET_STATUS.ARCHIVED, ASSET_STATUS.DEPRECATED],
  [ASSET_TYPE.QUESTION]: [ASSET_STATUS.ACTIVE, ASSET_STATUS.PENDING, ASSET_STATUS.DONE, ASSET_STATUS.ARCHIVED],
  [ASSET_TYPE.PROJECT]: [ASSET_STATUS.ACTIVE, ASSET_STATUS.PENDING, ASSET_STATUS.DONE, ASSET_STATUS.PAUSED, ASSET_STATUS.ARCHIVED],
  [ASSET_TYPE.KNOWLEDGE]: [ASSET_STATUS.DRAFT, ASSET_STATUS.ACTIVE, ASSET_STATUS.PENDING, ASSET_STATUS.ARCHIVED, ASSET_STATUS.DEPRECATED],
  [ASSET_TYPE.TODO]: [ASSET_STATUS.ACTIVE, ASSET_STATUS.PENDING, ASSET_STATUS.DONE, ASSET_STATUS.PAUSED, ASSET_STATUS.ARCHIVED],
  [ASSET_TYPE.INTEREST]: [ASSET_STATUS.ACTIVE, ASSET_STATUS.PENDING, ASSET_STATUS.DONE, ASSET_STATUS.ARCHIVED],
};

export const INPUT_MODE_DETAILS = {
  input: {
    title: 'Sink Mode',
    description: '思いついたことを沈めて残すモード。AI応答せず、Rawログを保持します。',
    accent: 'border-blue-500/30 bg-blue-950/20 text-blue-200',
  },
  discuss: {
    title: 'AI Buddy',
    description: '壁打ち相手として問いを返すモード。NoteやAssetへ育てる思考整理に使います。',
    accent: 'border-indigo-500/30 bg-indigo-950/20 text-indigo-200',
  },
  anti: {
    title: 'Anti Buddy',
    description: 'あえて反対側から突くモード。弱点、前提の抜け、反証を出して判断基準を鍛えます。',
    accent: 'border-rose-500/30 bg-rose-950/20 text-rose-200',
  },
};

export const SETTINGS_KEYS = {
  provider: 'secondBrain.llmProvider',
  model: 'secondBrain.llmModel',
  modelLight: 'secondBrain.llmModelLight',
  modelHeavy: 'secondBrain.llmModelHeavy',
  openaiKey: 'secondBrain.openaiApiKey',
  anthropicKey: 'secondBrain.anthropicApiKey',
  geminiKey: 'secondBrain.geminiApiKey',
  debug: 'secondBrain.debugMode',
};

export const LIGHT_ACTIONS = new Set([ACTION_TYPE.CHAT_REPLY, ACTION_TYPE.CONTEXT_SELECT, ACTION_TYPE.MEMOIZE]);
export const HEAVY_ACTIONS = new Set([ACTION_TYPE.EXTRACT_INSIGHT, ACTION_TYPE.TAG_EXTRACT_INSIGHT, ACTION_TYPE.AUDIT]);

export const INSIGHT_STATUS_FILTER_LABELS = {
  [INSIGHT_STATUS.SUGGESTED]: '未処理',
  [INSIGHT_STATUS.ACCEPTED]: 'Asset化済み',
  [INSIGHT_STATUS.REJECTED]: '却下',
  all: 'すべて',
};

export const INSIGHT_STATUS_FILTER_OPTIONS = [
  [INSIGHT_STATUS.SUGGESTED, INSIGHT_STATUS_FILTER_LABELS[INSIGHT_STATUS.SUGGESTED]],
  [INSIGHT_STATUS.ACCEPTED, INSIGHT_STATUS_FILTER_LABELS[INSIGHT_STATUS.ACCEPTED]],
  [INSIGHT_STATUS.REJECTED, INSIGHT_STATUS_FILTER_LABELS[INSIGHT_STATUS.REJECTED]],
  ['all', INSIGHT_STATUS_FILTER_LABELS.all],
];

export const ASSET_STATUS_FILTER_LABELS = {
  [ASSET_STATUS.ACTIVE]: '使用中',
  [ASSET_STATUS.PENDING]: '保留',
  [ASSET_STATUS.DRAFT]: '下書き',
  all: 'すべて',
};

export const ASSET_STATUS_FILTER_OPTIONS = [
  [ASSET_STATUS.ACTIVE, ASSET_STATUS_FILTER_LABELS[ASSET_STATUS.ACTIVE]],
  [ASSET_STATUS.PENDING, ASSET_STATUS_FILTER_LABELS[ASSET_STATUS.PENDING]],
  [ASSET_STATUS.DRAFT, ASSET_STATUS_FILTER_LABELS[ASSET_STATUS.DRAFT]],
  ['all', ASSET_STATUS_FILTER_LABELS.all],
];

export const USAGE_CATEGORIES = [
  { key: 'buddy', label: 'Buddy', actions: [ACTION_TYPE.CHAT_REPLY, ACTION_TYPE.CONTEXT_SELECT], color: 'bg-blue-500' },
  { key: 'note',  label: 'Note',  actions: [ACTION_TYPE.MEMOIZE],                                      color: 'bg-emerald-500' },
  { key: 'hint',  label: 'Hint',  actions: [ACTION_TYPE.EXTRACT_INSIGHT],                              color: 'bg-purple-500' },
  { key: 'tag',   label: 'Tag',   actions: [ACTION_TYPE.TAG_EXTRACT_INSIGHT],                          color: 'bg-indigo-400' },
  { key: 'audit', label: 'Audit', actions: [ACTION_TYPE.AUDIT],                                        color: 'bg-amber-500' },
];

export const PROVIDER_MODELS = {
  gemini:    ['gemini-3.5-flash', 'gemini-3.1-flash-lite', 'gemini-2.5-flash-lite', 'gemini-2.5-flash', 'gemini-2.5-pro'],
  openai:    ['gpt-4.1', 'gpt-4.1-mini', 'gpt-4o', 'gpt-4o-mini'],
  anthropic: ['claude-opus-4-7', 'claude-sonnet-4-6', 'claude-haiku-4-5-20251001'],
};


export const ACTION_TYPE_LABELS = {
  [ACTION_TYPE.CHAT_REPLY]: 'AI Buddy',
  [ACTION_TYPE.MEMOIZE]: 'Noteまとめ',
  [ACTION_TYPE.EXTRACT_INSIGHT]: 'Hint抽出',
  [ACTION_TYPE.TAG_EXTRACT_INSIGHT]: 'Hint一括抽出',
  [ACTION_TYPE.NOTE_UPDATE]: 'Note更新候補',
  [ACTION_TYPE.ASSET_UPDATE]: 'Asset更新候補',
};

export const CREDIT_UX_PATTERN = 'B'; // 'A', 'B', or 'C'
export const CREDIT_WARNING_THRESHOLD = 0.8;

export const LINK_RELATION = Object.freeze({
  SOURCE_OF: 'source_of',
  RELATED: 'related',
  SUPPORTS: 'supports',
  CONTRADICTS: 'contradicts',
  DEVELOPS: 'develops',
});

export const LINK_CREATED_BY = Object.freeze({
  USER: 'user',
  AI: 'ai',
});
